import React, { useCallback, useState } from 'react';
import { gql } from '@apollo/client';
import { useDispatch, useSelector } from 'react-redux';
import { useBlockingRouter } from '../../../../hooks/useBlockingRouter';
import {
	InvoiceStatus,
	InvoiceViewButtonBarSendResendButtonInvoiceFragment,
	UserType,
	useInvoiceViewButtonBar_SendOrResendInvoiceMutation,
} from '#types';
import { ButtonBarButtonsProp } from '../../../ButtonBar';
import { ButtonGroupButtonProps } from '../../../ButtonGroupButton';
import { Icon } from '../../../../blocks/icons';
import MuiPayToModal from '../../../../mui/MuiModal/MuiPayToModal/MuiPayToModal';
import { useToast } from '../../../../hooks/useToast';
import { createBundleObject } from '../../../../lib/helpers';
import { displayStripeNeedsModal } from '../../../../redux/actions';
import { GraphQLFormattedError } from 'graphql';
import rpcShared from '@rockpapercoin/rpc-shared';
import { FlattenedUser } from '../../../../types/user';
import { useTrackInvoiceSent } from '../../../../lib/helpers/invoice';
import {
	CustomizeCustomerNotificationModal,
	CustomizedEmail,
	MessagingType,
} from '../../../CustomizeCustomerNotificationModal';
import { isCreatingNotSendingDocument } from '../../../../lib/helpers/bundle/isCreatingNotSendingDocument';
import { DocumentTypeEnum } from '../../../SuggestDocumentCreateModal';
import { Differences } from '../../../../lib/helpers/deepCompareAndDiff';
import { useHeadsUpModal } from '../../../../hooks/useHeadsUpModal';

export const invoiceViewButtonBarSendResendButtonInvoiceFragment = gql`
	fragment invoiceViewButtonBarSendResendButtonInvoice on Invoice {
		id
		status
		automatedRemindersEnabled
		contact {
			id
			firstName
			...getViewingUserDocumentRelationshipContact
		}
		lineItems {
			id
			rate
			quantity
			taxPercentage
		}
		discountAmount
		paymentInstallments {
			id
			status
			dueDate
		}
		organization {
			...getViewingUserDocumentRelationshipOrganization
		}
	}
	${ rpcShared.contacts.getViewingUserDocumentRelationshipContactFragment }
	${ rpcShared.organizations
		.getViewingUserDocumentRelationshipOrganizationFragment }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const invoiceViewButtonBar_sendOrResendInvoice = gql`
	mutation invoiceViewButtonBar_sendOrResendInvoice(
		$where: InvoiceWhereUniqueInput!
		$data: SendOrResendInvoiceInput
	) {
		sendOrResendInvoice(where: $where, data: $data) {
			id
			status
			title
			totalAmount
			lineItems {
				id
				rate
				quantity
				taxPercentage
			}
			discountAmount
			paymentInstallments {
				id
				status
				dueDate
			}
		}
	}
`;

type UseInvoiceViewButtonBarSendResendButtonReturnType = {
	button: ButtonBarButtonsProp['buttons'];
	jsx: React.JSX.Element;
};

type UseInvoiceViewButtonBarSendResendButtonProps = {
	invoice?: InvoiceViewButtonBarSendResendButtonInvoiceFragment;
	requestingUserBaseId?: string;
	invoiceEdits?: Differences;
};

export const useInvoiceViewButtonBarSendResendButton = ( {
	invoice,
	requestingUserBaseId,
	invoiceEdits,
}: UseInvoiceViewButtonBarSendResendButtonProps ): UseInvoiceViewButtonBarSendResendButtonReturnType => {
	const { showError, showSuccess } = useToast();
	const [ sendOrResendInvoice, { loading } ] =
		useInvoiceViewButtonBar_SendOrResendInvoiceMutation();
	const [ showPayToModal, setShowPayToModal ] = useState( false );
	const [ openCustomizeEmailModal, setOpenCustomizeEmailModal ] = useState( false );
	const user: FlattenedUser = useSelector( ( state: any ) => state?.user );
	const dispatch = useDispatch();
	const router = useBlockingRouter();
	const trackSegmentInvoiceSent = useTrackInvoiceSent(
		!!invoice?.status && invoice?.status !== InvoiceStatus.Draft
	);

	const { viewingUserIsAssignedMember } =
		rpcShared.contacts.getViewingUserDocumentRelationship( {
			contact: invoice?.contact,
			viewingUserId: requestingUserBaseId,
			permission: 'plannerOrgCanPayInvoices',
		} );
	const {
		viewingUserIsMember: viewingUserIsVendorMember,
		viewingUserIsAdminOrManager: viewingUserIsVendorAdminOrManager,
	} = rpcShared.organizations.getViewingUserDocumentRelationship( {
		organization: invoice?.organization,
		viewingUserId: requestingUserBaseId,
	} );

	const isActionable =
		invoice?.status &&
		( ( invoice?.id &&
			![
				InvoiceStatus.Draft,
				InvoiceStatus.Void,
				InvoiceStatus.Hidden
			].includes(
				invoice?.status
			) &&
			viewingUserIsAssignedMember ) ||
			( ![ InvoiceStatus.Void, InvoiceStatus.Hidden ].includes( invoice?.status ) &&
				viewingUserIsVendorAdminOrManager ) );

	const isVisible =
		( viewingUserIsVendorMember || viewingUserIsVendorAdminOrManager ) &&
		user?.isLoggedIn;

	const handleSendResendError = useCallback(
		( error: GraphQLFormattedError ) => {
			if (
				error.message.includes( rpcShared.strings.errorMessages.hasStripeNeeds )
			) {
				dispatch(
					displayStripeNeedsModal( {
						active: true,
						props: {
							purpose: 'sendInvoice',
							vendorName:
								user?.isLoggedIn && user.userType === UserType.OrgUser
									? user?.organization.name
									: '',
						},
					} )
				);
			} else if (
				error.message.includes(
					rpcShared.strings.errorMessages.hasStripeConnectNeeds
				)
			) {
				setShowPayToModal( true );
			} else {
				showError( error );
			}
		},
		[
			dispatch,
			showError,
			user
		]
	);

	const handleGoBack = useCallback( async () => {
		await router.push( { query: { ...router.query, tab: 'edit' } } );
	}, [ router ] );

	const performSend = useCallback(
		async ( customizedEmail?: CustomizedEmail ) => {
			if ( !invoice?.id ) {
				return false;
			}
			const bundle = invoice?.contact?.id
				? createBundleObject( invoice.contact?.id )
				: undefined;
			const sentRes = await sendOrResendInvoice( {
				variables: {
					where: { id: invoice.id },
					data: {
						...( bundle
							? {
								bundle: {
									contactID: bundle.contactID,
									contractBundleStatus: bundle.contractBundleStatus,
									invoiceBundleStatus: bundle.invoiceBundleStatus,
									proposalBundleStatus: bundle.proposalBundleStatus,
									eventDate: bundle.eventDate,
								},
							  }
							: {} ),
						customizedEmail,
					},
				},
			} );
			if ( sentRes?.data?.sendOrResendInvoice ) {
				showSuccess(
					invoice.status === InvoiceStatus.Sent
						? 'Invoice resent'
						: isCreatingNotSendingDocument( bundle, DocumentTypeEnum.Invoice )
							? 'Invoice created'
							: 'Invoice sent'
				);
				await trackSegmentInvoiceSent( sentRes.data.sendOrResendInvoice );
				return true;
			} else if ( sentRes?.errors ) {
				sentRes.errors.forEach( ( error ) => {
					handleSendResendError( error );
				} );
			} else {
				showError();
			}
			return false;
		},
		[
			invoice,
			sendOrResendInvoice,
			showSuccess,
			showError,
			trackSegmentInvoiceSent,
			handleSendResendError,
		]
	);

	const { showIfRemindersDisabled, headsUpModalJsx } = useHeadsUpModal( {
		onGotIt: performSend,
		onGoBack: handleGoBack,
	} );

	const onClick = useCallback(
		async (
			customizedEmailOrMouseEvent?:
				| CustomizedEmail
				| React.MouseEvent<HTMLElement, MouseEvent>
		) => {
			if (
				customizedEmailOrMouseEvent &&
				'intro' in customizedEmailOrMouseEvent
			) {
				return performSend( customizedEmailOrMouseEvent );
			}
			if ( showIfRemindersDisabled( invoice?.automatedRemindersEnabled ) ) {
				return false;
			}
			return performSend( undefined );
		},
		[
			invoice?.automatedRemindersEnabled,
			performSend,
			showIfRemindersDisabled
		]
	);

	const buttonText =
		invoice?.status === InvoiceStatus.Draft ? 'Send' : 'Resend';

	const invoiceTotalsAsStrings = invoice
		? rpcShared.invoices.calculatePaymentTotalsToString( invoice )
		: undefined;

	const buttonGroupOptions: Pick<
		ButtonGroupButtonProps,
		'options' | 'optionsLabel' | 'buttonProps'
	> = {
		optionsLabel: `Save and ${ buttonText.toLowerCase() }, or customize customer notification emails and send`,
		options: [
			{
				children: buttonText,
				onClick,
			},
			{
				children: `Customize & ${ buttonText.toLowerCase() }`,
				onClick: () => {
					if ( showIfRemindersDisabled( invoice?.automatedRemindersEnabled ) ) {
						return;
					}
					setOpenCustomizeEmailModal( true );
				},
			},
		],
		buttonProps: { onClick },
	};

	const button: ButtonBarButtonsProp['buttons'] = {
		[ buttonText ]: {
			isVisible,
			isIconWhenSmall: false,
			variant: 'contained',
			'data-cy': 'send-resend-button',
			loading,
			startIcon: <Icon type={ 'send' } />,
			...buttonGroupOptions,
			popover: invoiceEdits
				? 'Please save your edits before taking this action'
				: undefined,
			disabled: !!invoiceEdits || !invoice?.id || !isActionable,
		},
	};

	return {
		button,
		jsx: (
			<>
				<MuiPayToModal
					open={ showPayToModal }
					onClose={ () => setShowPayToModal( false ) }
				/>
				{ invoice?.contact?.firstName &&
				invoiceTotalsAsStrings?.unpaidBalance ? (
						<CustomizeCustomerNotificationModal
							type={
								invoice?.status === InvoiceStatus.Draft
									? MessagingType.InvoiceSent
									: MessagingType.InvoiceResent
							}
							unpaidBalance={ invoiceTotalsAsStrings?.unpaidBalance }
							open={ openCustomizeEmailModal }
							onClose={ () => setOpenCustomizeEmailModal( false ) }
							onSend={ onClick }
							firstName={ invoice.contact.firstName }
						/>
					) : null }
				{ headsUpModalJsx }
			</>
		),
	};
};
